/** Paginação do Documento: o que cabe em cada página.
 *
 *  Puro de propósito, e separado de quem escreve o PDF: a decisão de
 *  onde parte a página é onde estão as regras que se vêem a olho, e
 *  por isso é a parte que tem de ser testável.
 *
 *  Duas regras que fazem a diferença entre um relatório que parece
 *  feito e um que parece cuspido:
 *
 *   - um título nunca fica sozinho no fim de uma página. Se o que vem
 *     a seguir não cabe, desce com ele;
 *   - uma tabela parte-se entre páginas, mas repete o cabeçalho, senão
 *     a segunda metade fica sem se saber o que é cada coluna.
 */

import { helveticaWidthMm } from '@viewer/views2d/lib/dimensions'

export type Papel = 'A4' | 'A3'

export const PAPEL_MM: Record<Papel, { largura: number; altura: number }> = {
  A4: { largura: 210, altura: 297 },
  A3: { largura: 297, altura: 420 },
}

export const MARGEM_MM = 20
/** Espaço reservado em baixo para o rodapé com a paginação. */
export const RODAPE_MM = 12

export interface EstiloBloco {
  tamanhoMm: number
  negrito: boolean
  /** Espaço antes do bloco. */
  antesMm: number
  /** Espaço depois do bloco. */
  depoisMm: number
}

export const ESTILOS: Record<string, EstiloBloco> = {
  titulo: { tamanhoMm: 7, negrito: true, antesMm: 0, depoisMm: 5 },
  subtitulo: { tamanhoMm: 5, negrito: true, antesMm: 5, depoisMm: 2.5 },
  paragrafo: { tamanhoMm: 3.5, negrito: false, antesMm: 0, depoisMm: 3 },
  tabela: { tamanhoMm: 3.2, negrito: false, antesMm: 2, depoisMm: 3 },
}

/** Uma linha já resolvida e medida, pronta a ser escrita. */
export interface LinhaComposta {
  texto: string
  tamanhoMm: number
  negrito: boolean
  /** Distância ao topo da área útil, em mm. */
  yMm: number
  /** Recuo da esquerda, em mm. Usado nas colunas da tabela. */
  xMm: number
}

export interface PaginaComposta {
  linhas: LinhaComposta[]
}

/** Parte um texto em linhas que cabem na largura dada.
 *
 *  Uma palavra maior do que a linha inteira (um caminho de ficheiro,
 *  por exemplo) fica sozinha na sua linha e transborda, em vez de
 *  entrar num ciclo infinito à procura de um sítio onde partir. */
export function quebrarTexto(
  texto: string,
  larguraMm: number,
  tamanhoMm: number,
): string[] {
  const palavras = texto.split(/\s+/).filter(Boolean)
  if (palavras.length === 0) return ['']
  const linhas: string[] = []
  let atual = ''
  for (const palavra of palavras) {
    const tentativa = atual === '' ? palavra : `${atual} ${palavra}`
    if (helveticaWidthMm(tentativa, tamanhoMm) <= larguraMm || atual === '') {
      atual = tentativa
    } else {
      linhas.push(atual)
      atual = palavra
    }
  }
  if (atual !== '') linhas.push(atual)
  return linhas
}

/** Altura de uma linha, com entrelinha. */
export function alturaLinhaMm(tamanhoMm: number): number {
  return tamanhoMm * 1.45
}

export interface BlocoComposto {
  /** As linhas do bloco, já quebradas. */
  linhas: { texto: string; xMm: number }[]
  tamanhoMm: number
  negrito: boolean
  antesMm: number
  depoisMm: number
  /** Um título não pode ficar sozinho no fim da página. */
  ehTitulo: boolean
  /** As linhas de cabeçalho a repetir se o bloco partir de página. */
  cabecalho?: { texto: string; xMm: number }[]
}

/**
 * Distribui os blocos por páginas.
 *
 * `alturaUtilMm` é o que sobra depois das margens e do rodapé.
 */
export function paginar(
  blocos: readonly BlocoComposto[],
  alturaUtilMm: number,
): PaginaComposta[] {
  const paginas: PaginaComposta[] = []
  let linhas: LinhaComposta[] = []
  let y = 0

  const fecharPagina = () => {
    if (linhas.length > 0) paginas.push({ linhas })
    linhas = []
    y = 0
  }

  for (const bloco of blocos) {
    const alturaLinha = alturaLinhaMm(bloco.tamanhoMm)
    y += bloco.antesMm

    // Um título sozinho no fim da página fica órfão: precisa de caber
    // ele e da primeira linha do que vem a seguir.
    // Uma tabela precisa do cabeçalho e de pelo menos uma linha.
    const precisa = bloco.ehTitulo
      ? bloco.linhas.length * alturaLinha + alturaLinha
      : bloco.cabecalho
        ? 2 * alturaLinha
        : alturaLinha
    if (y + precisa > alturaUtilMm && linhas.length > 0) {
      fecharPagina()
      y += bloco.antesMm
    }

    // O cabeçalho da tabela abre o bloco; antes só aparecia nas páginas
    // de continuação.
    if (bloco.cabecalho) {
      for (const c of bloco.cabecalho) {
        linhas.push({ texto: c.texto, tamanhoMm: bloco.tamanhoMm, negrito: true, yMm: y, xMm: c.xMm })
      }
      y += alturaLinha
    }

    for (const linha of bloco.linhas) {
      if (y + alturaLinha > alturaUtilMm && linhas.length > 0) {
        fecharPagina()
        // Ao partir uma tabela, repete-se o cabeçalho na página nova.
        if (bloco.cabecalho) {
          for (const c of bloco.cabecalho) {
            linhas.push({
              texto: c.texto,
              tamanhoMm: bloco.tamanhoMm,
              negrito: true,
              yMm: y,
              xMm: c.xMm,
            })
          }
          y += alturaLinha
        }
      }
      linhas.push({
        texto: linha.texto,
        tamanhoMm: bloco.tamanhoMm,
        negrito: bloco.negrito,
        yMm: y,
        xMm: linha.xMm,
      })
      // Várias colunas na mesma linha partilham o mesmo y: só se
      // avança depois da última coluna dessa linha.
      const ultimaDaLinha =
        bloco.linhas.indexOf(linha) === bloco.linhas.length - 1 ||
        bloco.linhas[bloco.linhas.indexOf(linha) + 1]?.xMm === 0
      if (ultimaDaLinha) y += alturaLinha
    }
    y += bloco.depoisMm
  }

  fecharPagina()
  return paginas.length > 0 ? paginas : [{ linhas: [] }]
}
